import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useUpdateStudioProfile } from '../lib/queries';
import { useAuth } from '../lib/auth';
import { ApiError, type StudioProKind } from '../lib/api';

/**
 * Sprint 9.D — Onboarding post-register pro.
 * Mostra 3 card per scegliere pro_kind (nutri / coach / both) PRIMA che
 * il pro atterri sul dashboard (che con 0 clienti è "brutto" e non guidato).
 * POST update via useUpdateStudioProfile → /api/pro/profile.
 *
 * Skip dopo prima volta: se pro_kind != null in /api/auth/me, già configurato → /dashboard.
 */

interface KindOption {
  kind: StudioProKind;
  title: string;
  emoji: string;
  description: string;
  features: string[];
}

const OPTIONS: KindOption[] = [
  {
    kind: 'nutri',
    title: 'Nutrizionista',
    emoji: '🥗',
    description: 'Gestisci piani alimentari, visite e aderenza',
    features: ['Editor piani 4-step', 'AI suggest pasti', 'Heatmap aderenza', 'Spesa intelligente'],
  },
  {
    kind: 'coach',
    title: 'Coach personal trainer',
    emoji: '💪',
    description: 'Crea programmi workout, monitora PR e atleti',
    features: ['Programmi multi-settimana', 'Set log live', 'PR detection Brzycki', 'Recovery heatmap'],
  },
  {
    kind: 'both',
    title: 'Entrambi',
    emoji: '✨',
    description: 'Nutri + coach con role-switcher in app',
    features: ['Tutto incluso', 'Switch nutri↔coach dalla sidebar', 'Per studi multidisciplinari'],
  },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const updateMut = useUpdateStudioProfile();
  const [selected, setSelected] = useState<StudioProKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    await logout();
  };

  const handleContinue = () => {
    setError(null);
    if (!selected) {
      setError('Scegli una figura professionale per proseguire');
      return;
    }
    updateMut.mutate(
      { pro_kind: selected },
      {
        onSuccess: () => {
          // Marca onboarding completato in localStorage (UI hint)
          try { window.localStorage.setItem('dietamenu.pro.onboarded', '1'); } catch (e) {}
          navigate('/dashboard');
        },
        onError: (err) => {
          setError(err instanceof ApiError ? err.message : err.message || String(err));
        },
      }
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper, #f5f1e8)', padding: '60px 20px', position: 'relative' }}>
      {/* Sprint 9.D — logout sempre accessibile anche durante onboarding */}
      <div style={{ position: 'absolute', top: 20, right: 24, display: 'flex', gap: 12, alignItems: 'center' }}>
        {user?.email && (
          <span style={{ fontSize: 12, color: 'var(--mute)', fontFamily: 'var(--mono)' }}>
            {user.email}
          </span>
        )}
        <button
          type="button"
          onClick={handleLogout}
          disabled={loggingOut}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', border: '1px solid var(--line)', borderRadius: 999,
            background: 'transparent', cursor: loggingOut ? 'wait' : 'pointer',
            fontSize: 12, fontWeight: 600, color: 'var(--red, #c0392b)',
          }}
        >
          <LogOut size={14} />
          {loggingOut ? 'Uscita…' : 'Esci'}
        </button>
      </div>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, letterSpacing: 1.6, color: 'var(--mute)', textTransform: 'uppercase', marginBottom: 8 }}>
            Studio Pro · Benvenuto
          </div>
          <h1 style={{ fontFamily: 'var(--display)', fontSize: 36, fontWeight: 800, letterSpacing: -1, margin: 0, lineHeight: 1.1 }}>
            Cosa fai per i tuoi clienti?
          </h1>
          <p style={{ color: 'var(--mute)', fontSize: 16, marginTop: 12, maxWidth: 600, marginInline: 'auto' }}>
            Scegli il modulo che meglio descrive il tuo studio. Puoi cambiare in qualsiasi momento da{' '}
            <strong>Impostazioni → Figura professionale</strong>.
          </p>
        </div>

        {error && (
          <div role="alert" style={{ background: '#fff0f0', border: '1px solid var(--red, #c0392b)', borderRadius: 8, padding: 12, fontSize: 13, color: 'var(--red, #c0392b)', marginBottom: 24, maxWidth: 600, marginInline: 'auto' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 40 }}>
          {OPTIONS.map((opt) => {
            const isActive = selected === opt.kind;
            return (
              <button
                key={opt.kind}
                type="button"
                onClick={() => setSelected(opt.kind)}
                disabled={updateMut.isPending}
                style={{
                  background: isActive ? 'var(--green-deep, #0b8a3e)' : 'var(--paper, #fff)',
                  color: isActive ? '#fff' : 'var(--ink, #0f1410)',
                  border: isActive ? '3px solid var(--green-deep, #0b8a3e)' : '3px solid var(--line, #d8d4c8)',
                  borderRadius: 16,
                  padding: '24px 20px',
                  textAlign: 'left',
                  cursor: updateMut.isPending ? 'wait' : 'pointer',
                  transition: 'all 0.18s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  minHeight: 280,
                }}
              >
                <div style={{ fontSize: 40, lineHeight: 1 }}>{opt.emoji}</div>
                <div style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 800, marginTop: 4 }}>
                  {opt.title}
                </div>
                <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.4 }}>
                  {opt.description}
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0', fontSize: 12, lineHeight: 1.7 }}>
                  {opt.features.map((f, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: isActive ? '#fff' : 'var(--green-deep)' }}>✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!selected || updateMut.isPending}
            style={{
              background: selected && !updateMut.isPending ? 'var(--ink, #0f1410)' : 'var(--line, #d8d4c8)',
              color: '#fff',
              border: 'none',
              padding: '14px 36px',
              borderRadius: 999,
              fontFamily: 'var(--display)',
              fontSize: 16,
              fontWeight: 700,
              cursor: selected && !updateMut.isPending ? 'pointer' : 'not-allowed',
              minWidth: 220,
            }}
          >
            {updateMut.isPending ? 'Salvataggio…' : selected ? `Continua come ${OPTIONS.find(o => o.kind === selected)?.title}` : 'Scegli un\'opzione'}
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: 'var(--mute)' }}>
          Sai cosa fai? <button
            type="button"
            onClick={() => navigate('/dashboard')}
            style={{ background: 'transparent', border: 'none', color: 'var(--green-deep)', cursor: 'pointer', textDecoration: 'underline', fontSize: 12 }}
          >Salta e configura dopo</button>
        </div>
      </div>
    </div>
  );
}
